import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { PageHeader } from "@/components/layout/PageHeader";
import { DashboardView } from "@/components/dashboard/DashboardView";
import { SnapshotView } from "@/components/snapshot/SnapshotView";
import { CompareView } from "@/components/compare/CompareView";
import { CompareResultDetailPage } from "@/components/compare/CompareResultDetailPage";
import { BrandsView } from "@/components/brands/BrandsView";
import { PromptsView } from "@/components/prompts/PromptsView";
import { HistoryView } from "@/components/history/HistoryView";
import { InsightsView } from "@/components/insights/InsightsView";
import { TournamentView } from "@/components/tournament/TournamentView";
import { StudiesView } from "@/components/studies/StudiesView";
import { ReportsView } from "@/components/reports/ReportsView";
import { ReportsHubView } from "@/components/reports/ReportsHubView";
import { ReportResultsPage } from "@/components/reports/ReportResultsPage";
import { RollupsView } from "@/components/rollups/RollupsView";
import { AlertsView } from "@/components/alerts/AlertsView";
import { ExploreView } from "@/components/explore/ExploreView";
import { BlindView } from "@/components/blind/BlindView";
import { TopicPage } from "@/components/topics/TopicPage";
import { NewTopicWizard } from "@/components/topics/NewTopicWizard";
import { HelpView } from "@/components/help/HelpView";
import { LoginView } from "@/components/auth/LoginView";
import { api, setUnauthorizedHandler } from "@/api/client";
import { useWorkspacesStore } from "@/stores/workspaces";
import { cancelComparisonPolling } from "@/stores/comparisons";
import { Spinner } from "@/components/ui/spinner";

type View = "health" | "reports" | "explore" | "trends" | "compare" | "tournament" | "studies" | "narrative" | "rollups" | "alerts" | "brands" | "prompts" | "history" | "insights" | "blind" | "topics" | "topic-new" | "help";

const viewConfig: Record<View, { title: string; description: string }> = {
  health: {
    title: "Brand Health",
    description: "How your brand is doing across its topic spaces — fed by your tracking.",
  },
  reports: {
    title: "Reports",
    description: "Frozen, shareable artifacts — automated recaps and leadership snapshots.",
  },
  explore: {
    title: "Explore",
    description: "One-off experiments — ask anything, right now.",
  },
  trends: {
    title: "Trends",
    description: "The full metric explorer — any brand, any metric, any window.",
  },
  compare: {
    title: "Compare",
    description: "Measure how AI models perceive and rank brands across scoring dimensions.",
  },
  tournament: {
    title: "Tournament",
    description: "Round-robin head-to-head matchups to rank brands by wins.",
  },
  studies: {
    title: "Studies",
    description: "Track brand perception over time with repeated runs.",
  },
  narrative: {
    title: "Narrative reports",
    description: "AI-written cross-model sentiment reports you can share with a public link.",
  },
  rollups: {
    title: "Recaps",
    description: "Monthly, quarterly, and yearly recaps of how your brands trended.",
  },
  alerts: {
    title: "Alerts",
    description: "Threshold rules that watch your metrics and notify you when they slip.",
  },
  brands: {
    title: "Brands",
    description: "Who you track — your brand and the competitors it's measured against.",
  },
  prompts: {
    title: "Prompts & topics",
    description: "What gets asked — the questions Prism runs against AI models, grouped into Topics.",
  },
  history: {
    title: "Activity",
    description: "All past comparisons and their results.",
  },
  insights: {
    title: "Insights",
    description: "Win rates, score trends, and performance over time.",
  },
  blind: {
    title: "Blind Queries",
    description: "What AI shows when nobody asks about your brands — share of voice from neutral questions.",
  },
  topics: {
    title: "Topic space",
    description: "Everything tracked for one Topic — prompts, competitors, tracking, and the answers themselves.",
  },
  "topic-new": {
    title: "New topic",
    description: "Set up a topic space — name it, add prompts, pick competitors, and turn on tracking.",
  },
  help: {
    title: "Help & Documentation",
    description: "Guides for how Prism works and how to use every part of it.",
  },
};

type AuthState = "checking" | "unauthenticated" | "authenticated";

function AppPage({ view, children, noHeader }: { view: View; children: ReactNode; noHeader?: boolean }) {
  const currentId = useWorkspacesStore(s => s.currentId);
  const config = viewConfig[view];
  const location = useLocation();
  const isCompareResults = location.pathname.match(/^\/app\/compare\/results\//);

  return (
    <div className="max-w-[1152px] mx-auto px-8 py-10">
      {/* Some views (the topic page) own a dynamic header the static viewConfig
          can't render, so they opt out of the shared PageHeader. */}
      {!noHeader && <PageHeader title={config.title} description={config.description} as={isCompareResults ? "h2" : "h1"} />}
      <div className={noHeader ? undefined : "mt-8"} key={currentId}>
        {children}
      </div>
    </div>
  );
}

function AppNotFound() {
  return (
    <div className="max-w-[640px] mx-auto px-8 py-16">
      <h1 className="text-2xl font-bold text-brand-950">Page not found</h1>
      <p className="mt-2 text-sm text-slate-500">
        This app page does not exist. Use the sidebar to choose a destination.
      </p>
    </div>
  );
}

function AuthenticatedAppShell({ onLogout }: { onLogout: () => void }) {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen overflow-hidden bg-[#f8fafc]">
      <Sidebar
        onWorkspaceSwitch={() => navigate("/app/health")}
        onLogout={onLogout}
      />
      <main className="flex-1 overflow-y-auto scrollbar-thin">
        <Routes>
          <Route index element={<Navigate to="health" replace />} />
          {/* The four destinations */}
          <Route path="health" element={<AppPage view="health"><SnapshotView /></AppPage>} />
          <Route path="reports" element={<AppPage view="reports"><ReportsHubView /></AppPage>} />
          <Route path="explore" element={<AppPage view="explore"><ExploreView /></AppPage>} />
          {/* Tracking Plan page retired — tracking now lives per topic space. */}
          <Route path="plan" element={<Navigate to="/app/health" replace />} />
          {/* Drill-ins and tools, reachable from the destinations (not top-level nav) */}
          <Route path="trends" element={<AppPage view="trends"><DashboardView /></AppPage>} />
          <Route path="compare" element={<AppPage view="compare"><CompareView /></AppPage>} />
          <Route path="compare/results/:id" element={<AppPage view="compare"><CompareResultDetailPage /></AppPage>} />
          <Route path="tournament" element={<AppPage view="tournament"><TournamentView /></AppPage>} />
          <Route path="studies" element={<AppPage view="studies"><StudiesView /></AppPage>} />
          <Route path="studies/:studyId" element={<AppPage view="studies"><StudiesView /></AppPage>} />
          {/* Static "narrative" must be declared alongside the :reportId matcher; the router ranks it higher. */}
          <Route path="reports/narrative" element={<AppPage view="narrative"><ReportsView /></AppPage>} />
          <Route path="reports/:reportId" element={<AppPage view="narrative"><ReportResultsPage /></AppPage>} />
          <Route path="rollups" element={<AppPage view="rollups"><RollupsView /></AppPage>} />
          <Route path="rollups/:rollupId" element={<AppPage view="rollups"><RollupsView /></AppPage>} />
          <Route path="alerts" element={<AppPage view="alerts"><AlertsView /></AppPage>} />
          <Route path="brands" element={<AppPage view="brands"><BrandsView /></AppPage>} />
          <Route path="prompts" element={<AppPage view="prompts"><PromptsView /></AppPage>} />
          <Route path="help" element={<AppPage view="help"><HelpView /></AppPage>} />
          <Route path="help/:sectionId" element={<AppPage view="help"><HelpView /></AppPage>} />
          <Route path="history" element={<AppPage view="history"><HistoryView /></AppPage>} />
          <Route path="insights" element={<AppPage view="insights"><InsightsView /></AppPage>} />
          <Route path="blind" element={<AppPage view="blind"><BlindView /></AppPage>} />
          {/* Static "new" must precede the :ref matcher so it isn't parsed as a topic ref. */}
          <Route path="topics/new" element={<AppPage view="topic-new"><NewTopicWizard /></AppPage>} />
          <Route path="topics/:ref" element={<AppPage view="topics" noHeader><TopicPage /></AppPage>} />
          {/* Old destinations keep working */}
          <Route path="dashboard" element={<Navigate to="/app/trends" replace />} />
          <Route path="snapshot" element={<Navigate to="/app/health" replace />} />
          <Route path="*" element={<AppNotFound />} />
        </Routes>
      </main>
    </div>
  );
}

function NonAppRouteNotice() {
  const location = useLocation();

  if (location.pathname === "/") {
    return <Navigate to="/app/health" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] px-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold text-brand-950">Route not handled by the app</h1>
        <p className="mt-2 text-sm text-slate-500">
          App routes live under /app. Public report pages remain server-owned at /reports/:slug.
        </p>
      </div>
    </div>
  );
}

export function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");

  const checkSession = async () => {
    try {
      const session = await api.auth.session();
      if (!session.auth_required || session.authenticated) {
        setAuthState("authenticated");
      } else {
        setAuthState("unauthenticated");
      }
    } catch {
      setAuthState("unauthenticated");
    }
  };

  useEffect(() => {
    checkSession();
    setUnauthorizedHandler(() => {
      cancelComparisonPolling();
      setAuthState("unauthenticated");
    });
  }, []);

  const handleLoggedIn = () => setAuthState("authenticated");
  const handleLogout = async () => {
    await api.auth.logout();
    setAuthState("unauthenticated");
  };

  if (authState === "checking") {
    return (
      <div className="h-screen flex items-center justify-center bg-[#f8fafc]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <LoginView onLoggedIn={handleLoggedIn} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/app/health" replace />} />
      <Route path="/app/*" element={<AuthenticatedAppShell onLogout={handleLogout} />} />
      <Route path="*" element={<NonAppRouteNotice />} />
    </Routes>
  );
}
